import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  LogOut, 
  User, 
  BarChart3, 
  Menu,
  X,
  History,
  Shield
} from 'lucide-react';

const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = () => {
    logout();
  };

  const isActiveRoute = (path) => {
    return location.pathname === path;
  };

  const handleNavigation = (path) => {
    navigate(path);
    setSidebarOpen(false);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile sidebar */}
      <div className={`fixed inset-0 z-50 lg:hidden ${sidebarOpen ? 'block' : 'hidden'}`}>
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setSidebarOpen(false)} />
        <div className="fixed inset-y-0 left-0 flex w-64 flex-col bg-white">
          <div className="flex h-16 items-center justify-between px-4">
            <h1 className="text-xl font-semibold text-gray-900">Options Trading</h1>
            <button
              onClick={() => setSidebarOpen(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
          <nav className="flex-1 space-y-1 px-2 py-4">
            <button
              onClick={() => handleNavigation('/dashboard')}
              className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md cursor-pointer w-full text-left ${
                isActiveRoute('/dashboard') 
                  ? 'text-gray-900 bg-gray-100' 
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }`}
            >
              <BarChart3 className="mr-3 h-5 w-5 text-gray-500" />
              Dashboard
            </button>
            <button
              onClick={() => handleNavigation('/historical')}
              className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md cursor-pointer w-full text-left ${
                isActiveRoute('/historical') 
                  ? 'text-gray-900 bg-gray-100' 
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }`}
            >
              <History className="mr-3 h-5 w-5 text-gray-500" />
              Historical Backtesting
            </button>
            {user?.is_admin && (
              <button
                onClick={() => handleNavigation('/admin')}
                className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md cursor-pointer w-full text-left ${
                  isActiveRoute('/admin') 
                    ? 'text-gray-900 bg-gray-100' 
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                <Shield className="mr-3 h-5 w-5 text-gray-500" />
                Admin Panel
              </button>
            )}
          </nav>
        </div>
      </div>

      {/* Static sidebar for desktop */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col">
        <div className="flex flex-col flex-grow bg-white border-r border-gray-200">
          <div className="flex items-center h-16 px-4">
            <h1 className="text-xl font-semibold text-gray-900">Options Trading</h1>
          </div>
          <nav className="flex-1 space-y-1 px-2 py-4">
            <button
              onClick={() => handleNavigation('/dashboard')}
              className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md cursor-pointer w-full text-left ${
                isActiveRoute('/dashboard') 
                  ? 'text-gray-900 bg-gray-100' 
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }`}
            >
              <BarChart3 className="mr-3 h-5 w-5 text-gray-500" />
              Dashboard
            </button>
            <button
              onClick={() => handleNavigation('/historical')}
              className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md cursor-pointer w-full text-left ${
                isActiveRoute('/historical') 
                  ? 'text-gray-900 bg-gray-100' 
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }`}
            >
              <History className="mr-3 h-5 w-5 text-gray-500" />
              Historical Backtesting
            </button>
            {user?.is_admin && (
              <button
                onClick={() => handleNavigation('/admin')}
                className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md cursor-pointer w-full text-left ${
                  isActiveRoute('/admin') 
                    ? 'text-gray-900 bg-gray-100' 
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                <Shield className="mr-3 h-5 w-5 text-gray-500" />
                Admin Panel
              </button>
            )}
          </nav>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top navigation */}
        <div className="sticky top-0 z-40 flex h-16 shrink-0 items-center gap-x-4 border-b border-gray-200 bg-white px-4 shadow-sm sm:gap-x-6 sm:px-6 lg:px-8">
          <button
            type="button"
            className="-m-2.5 p-2.5 text-gray-700 lg:hidden"
            onClick={() => setSidebarOpen(true)}
          >
            <span className="sr-only">Open sidebar</span>
            <Menu className="h-6 w-6" />
          </button>

          <div className="flex flex-1 gap-x-4 self-stretch lg:gap-x-6">
            <div className="flex flex-1"></div>
            <div className="flex items-center gap-x-4 lg:gap-x-6">
              {/* User menu */}
              <div className="relative">
                <div className="flex items-center space-x-3">
                  <div className="flex items-center space-x-3">
                    <div className="h-8 w-8 rounded-full bg-primary-100 flex items-center justify-center">
                      <User className="h-4 w-4 text-primary-600" />
                    </div>
                    <div className="hidden md:block">
                      <div className="text-sm font-medium text-gray-900">
                        {user?.username}
                      </div>
                      <div className="text-xs text-gray-500">
                        {user?.email}
                      </div>
                      <div className="text-xs text-blue-600">
                        {user?.is_admin ? 'Admin User' : 'Regular User'}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={handleLogout}
                    className="flex items-center space-x-2 text-sm text-gray-600 hover:text-gray-900"
                  >
                    <LogOut className="h-4 w-4" />
                    <span className="hidden md:block">Logout</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Page content */}
        <main className="py-6">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

export default Layout; 